import type Alert from "@/interfaces/Alerts/Alert";
import type ConfigurationFileContents from "@/interfaces/Configuration/ConfigurationFileContents";
import type Notification from "@/interfaces/Notifications/Notification";
import type UserProfileBasedSecurity from "@/interfaces/Security/UserProfileBasedSecurity";
import type WebLoginAccount from "@/interfaces/WebLogin/WebLoginAccount";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthStore } from "@/stores/auth";
import { useConfigurationStore } from "@/stores/configuration";
import { useNotificationsStore } from "@/stores/notifications";
import { useParametersStore } from "@/stores/parameters";

function createGetterModule<T extends Record<string, unknown>>(factory: () => T) {
  return new Proxy(
    {},
    {
      get(_target, property) {
        return factory()[property as keyof T];
      },
    }
  ) as T;
}

const storeGetters = {
  alertsModule: createGetterModule(() => {
    const alertsStore = useAlertsStore();
    return {
      alert: alertsStore.alert,
    };
  }),
  configurationModule: createGetterModule(() => {
    const configurationStore = useConfigurationStore();
    return {
      configurationContents: configurationStore.configurationContents,
    };
  }),
  notificationsModule: createGetterModule(() => {
    const notificationsStore = useNotificationsStore();
    return {
      notification: notificationsStore.notification,
    };
  }),
  parametersModule: createGetterModule(() => {
    const parametersStore = useParametersStore();
    return {
      parameterSettings: parametersStore.parameterSettings,
      entityParameterSettings: parametersStore.entityParameterSettings,
      isLoaded: parametersStore.isLoaded,
      isEntityParametersLoaded: parametersStore.isEntityParametersLoaded,
      isLoading: parametersStore.isAnyLoading,
    };
  }),
  webLoginModule: createGetterModule(() => {
    const authStore = useAuthStore();
    return {
      selectedAccount: authStore.selectedAccount,
      webLogin: authStore.webLogin,
      tokens: authStore.tokens,
      userProfileBasedSecurity: authStore.userProfileBasedSecurity,
    };
  }),
};

const storeDispatch = {
  alertsModule: {
    setAlert(alert: Alert | undefined) {
      return useAlertsStore().setAlert(alert);
    },
  },
  configurationModule: {
    setConfigurationContents(configurationContents: ConfigurationFileContents) {
      return useConfigurationStore().setConfigurationContents(configurationContents);
    },
  },
  notificationsModule: {
    setNotification(notification: Notification | undefined) {
      return useNotificationsStore().setNotification(notification);
    },
  },
  parametersModule: {
    setParameterSettings() {
      return useParametersStore().loadParameterSettings(true);
    },
    setEntityParameterSettings() {
      return useParametersStore().loadEntityParameterSettings(true);
    },
  },
  webLoginModule: {
    setSelectedAccount(selectedAccount: WebLoginAccount | undefined) {
      return useAuthStore().setSelectedAccount(selectedAccount);
    },
    setWebToken(tokens: WebLoginAuthenticationTokens | undefined) {
      return useAuthStore().setWebTokens(tokens);
    },
    setUserProfileBasedSecurity(
      userProfileBasedSecurity: UserProfileBasedSecurity | undefined
    ) {
      return useAuthStore().setUserProfileBasedSecurity(userProfileBasedSecurity);
    },
    restoreTokens() {
      return useAuthStore().restoreSession();
    },
    restoreUserBasedSecurity() {
      return useAuthStore().restoreSession();
    },
    clearState() {
      return useAuthStore().clearState();
    },
  },
};

const compatStore = {
  get getters() {
    return storeGetters;
  },
  get dispatch() {
    return storeDispatch;
  },
};

export default compatStore;

export type AppStore = typeof compatStore;
